import type { ReactNode } from 'react';
import type { AuditLogEntry } from '@core/api/auditLog';

export type AuditLogRenderer = (entry: AuditLogEntry) => ReactNode;

export function formatAuditFieldName(field: string): string {
  return field
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function isEntityReference(
  value: unknown
): value is { id?: unknown; label: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'label' in value &&
    typeof (value as { label: unknown }).label === 'string'
  );
}

export function formatAuditLogDisplayValue(value: unknown): string {
  if (value == null) {
    return '';
  }
  if (isEntityReference(value)) {
    return value.label;
  }
  if (Array.isArray(value)) {
    if (value.length > 0 && value.every(isEntityReference)) {
      return value.map((item) => item.label).join(', ');
    }
    return JSON.stringify(value, null, 2);
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return formatAuditFieldName(String(value));
}

function formatItemSubjectLabel(subjectType: string): string {
  const base = subjectType.endsWith('Item')
    ? subjectType.slice(0, -'Item'.length)
    : subjectType;
  return `${base} item`;
}

export function formatEntityTypeLabel(subjectType: string): string {
  if (subjectType.endsWith('Item')) {
    return formatItemSubjectLabel(subjectType);
  }
  return formatAuditFieldName(subjectType);
}

export function formatAuditSubjectDescription(entry: AuditLogEntry): {
  typeLabel: string;
  idPart: string;
} {
  const typeLabel = formatEntityTypeLabel(entry.subjectType);
  if (
    entry.subjectType === 'User' &&
    typeof entry.metadata?.username === 'string' &&
    entry.metadata.username
  ) {
    return {
      typeLabel: 'User',
      idPart: ` ${entry.metadata.username}`,
    };
  }
  const idPart = entry.subjectId != null ? ` #${entry.subjectId}` : '';
  return { typeLabel, idPart };
}

export function hasAuditLogDetails(entry: AuditLogEntry): boolean {
  return Boolean(
    Object.keys(entry.changes ?? {}).length ||
      Object.keys(entry.metadata ?? {}).length
  );
}
